import { House, MapPin, Ruler } from "lucide-react"

interface HousingFactsProps {
  floorAreaM2?: number
  houseType?: string
  location?: string
  statusLabel?: string
  hiddenValueUnit?: string | null
}

export function HousingFacts({
  floorAreaM2,
  houseType,
  location,
  statusLabel,
  hiddenValueUnit,
}: HousingFactsProps) {
  if (!floorAreaM2 || !houseType) return null

  const showFloorArea = !["m²", "m2", "sq m", "ft²", "ft2", "sq ft"].includes(
    hiddenValueUnit?.trim().toLowerCase() ?? ""
  )
  const gridColumns = location && showFloorArea
    ? "grid-cols-2 sm:grid-cols-3"
    : location || showFloorArea ? "grid-cols-2" : "grid-cols-1"

  const formattedMetricArea = new Intl.NumberFormat("en", {
    maximumFractionDigits: 1,
  }).format(floorAreaM2)
  const formattedImperialArea = new Intl.NumberFormat("en", {
    maximumFractionDigits: 0,
  }).format(floorAreaM2 * 10.7639)

  return (
    <div className="mx-auto mb-2 w-full max-w-xl sm:mb-4">
      {statusLabel && (
        <p className="mx-auto mb-1 w-fit rounded-full border border-white/25 bg-black/60 px-3 py-0 text-[10px] font-bold uppercase tracking-[0.14em] text-white/80 shadow-[0_8px_24px_rgba(0,0,0,0.2)] backdrop-blur-md sm:mb-2 sm:py-1">
          {statusLabel}
        </p>
      )}
      <dl
        className={`grid overflow-hidden rounded-xl border border-white/30 bg-black/65 text-left shadow-[0_12px_32px_rgba(0,0,0,0.3)] backdrop-blur-md ${gridColumns}`}
      >
        {location && (
          <div className={`flex min-w-0 items-center gap-2.5 px-3 py-1 sm:px-4 sm:py-2.5 ${showFloorArea ? "col-span-2 border-b border-white/15 sm:col-span-1 sm:border-b-0" : ""}`}>
            <MapPin className="size-4 shrink-0 text-[#ff8b7f]" aria-hidden="true" />
            <div className="min-w-0">
              <dt className="text-[9px] font-bold uppercase tracking-[0.13em] text-white/55 sm:text-[10px]">
                Location
              </dt>
              <dd className="text-sm font-bold leading-tight text-white md:text-base">
                {location}
              </dd>
            </div>
          </div>
        )}
        {showFloorArea && <div
          className={`flex min-w-0 items-center gap-2.5 px-3 py-1 sm:px-4 sm:py-2.5 ${
            location ? "sm:border-l sm:border-white/15" : ""
          }`}
        >
          <Ruler className="size-4 shrink-0 text-[#ff8b7f]" aria-hidden="true" />
          <div className="min-w-0">
            <dt className="text-[9px] font-bold uppercase tracking-[0.13em] text-white/55 sm:text-[10px]">
              Total area
            </dt>
            <dd className="text-sm font-bold leading-tight text-white md:text-base">
              {formattedMetricArea} m²
            </dd>
            <dd className="mt-0.5 text-[11px] font-semibold text-white/65 md:text-xs">
              {formattedImperialArea} ft²
            </dd>
          </div>
        </div>}
        <div className={`flex min-w-0 items-center gap-2.5 px-3 py-1 sm:px-4 sm:py-2.5 ${location || showFloorArea ? "border-l border-white/15" : ""}`}>
          <House className="size-4 shrink-0 text-[#ff8b7f]" aria-hidden="true" />
          <div className="min-w-0">
            <dt className="text-[9px] font-bold uppercase tracking-[0.13em] text-white/55 sm:text-[10px]">
              House type
            </dt>
            <dd className="whitespace-normal text-sm font-bold leading-tight text-white md:text-base">
              {houseType}
            </dd>
          </div>
        </div>
      </dl>
    </div>
  )
}
